import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import LayoutNew from "../../components/shared/LayoutNew";
import DesktopShell from "../../components/shared/DesktopShell";

/* ───────────────────────────────────────────────────────────────────────────
 * Shared mobile UI primitives for the Account pages, matching the reference
 * kingexch111.com layout: a light-grey filter card (dropdowns + date range +
 * action button) and a dark-blue titled results table.
 * ─────────────────────────────────────────────────────────────────────────── */

// Desktop breakpoint gate shared by AccountLayout and PageShell so the account
// pages render inside the desktop three-column shell while mobile keeps its
// original fixed-header/bottom-nav clearances byte-identical.
const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const media = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  return isDesktop;
};

// Wraps every account page: desktop -> LayoutNew desktopHome + DesktopShell
// (header + left Sports sidebar + right Open Bets), mobile -> the original
// plain LayoutNew tree. Children stay the same in both branches.
export const AccountLayout = ({ children }) => {
  const isDesktop = useIsDesktop();
  return isDesktop ? (
    <LayoutNew desktopHome>
      <DesktopShell activeNav="">{children}</DesktopShell>
    </LayoutNew>
  ) : (
    <LayoutNew>{children}</LayoutNew>
  );
};

export const PageShell = ({ children }) => {
  const isDesktop = useIsDesktop();
  return (
    <div
      style={{
        marginTop: isDesktop ? 0 : "55px",
        background: "#efefef",
        minHeight: isDesktop ? "auto" : "calc(100vh - 55px)",
        paddingBottom: isDesktop ? 0 : "70px",
      }}
    >
      {children}
    </div>
  );
};

export const selectStyle = {
  width: "100%",
  height: "40px",
  padding: "0 10px",
  border: "1px solid #ccc",
  borderRadius: "4px",
  background: "#fff",
  fontSize: "14px",
  color: "#333",
  appearance: "auto",
};

export const dateStyle = {
  width: "100%",
  height: "40px",
  padding: "0 10px",
  border: "1px solid #ccc",
  borderRadius: "4px",
  background: "#eee",
  fontSize: "14px",
  color: "#333",
};

/* Date field matching the reference: DD-MM-YYYY text + a filled calendar icon
   that opens a custom month-grid picker (Monday-first, blue dates, red Sundays,
   out-of-month days greyed, selected day underlined). */
export const DateField = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() =>
    (value ? moment(value) : moment()).clone().startOf("month")
  );
  const [hover, setHover] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const display = value ? moment(value).format("DD-MM-YYYY") : "";

  const toggle = () => {
    if (!open) setView((value ? moment(value) : moment()).clone().startOf("month"));
    setOpen((o) => !o);
  };

  const selectDay = (m) => {
    onChange(m.format("YYYY-MM-DD"));
    setOpen(false);
  };

  const firstWeekday = (view.clone().startOf("month").isoWeekday() + 6) % 7;
  const gridStart = view.clone().startOf("month").subtract(firstWeekday, "days");
  const cells = Array.from({ length: 42 }, (_, i) => gridStart.clone().add(i, "days"));
  const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div onClick={toggle} style={{ display: "flex", alignItems: "center", height: "40px", cursor: "pointer" }}>
        <span
          style={{
            flex: 1,
            height: "40px",
            lineHeight: "40px",
            padding: "0 12px",
            border: "1px solid #c9d0d0",
            borderRight: "none",
            borderRadius: "4px 0 0 4px",
            background: "#dfe4e4",
            fontSize: "14px",
            color: "#444",
            boxSizing: "border-box",
          }}
        >
          {display}
        </span>
        <span
          style={{
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid #b7c0c0",
            borderRadius: "0 4px 4px 0",
            background: "#cdd4d4",
            boxSizing: "border-box",
          }}
        >
          {/* Filled calendar glyph with day dots, matching the reference */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <line x1="7" y1="2.5" x2="7" y2="6" stroke="#2b2b2b" strokeWidth="2" strokeLinecap="round" />
            <line x1="17" y1="2.5" x2="17" y2="6" stroke="#2b2b2b" strokeWidth="2" strokeLinecap="round" />
            <rect x="3" y="4.5" width="18" height="17" rx="2.5" fill="#2b2b2b" />
            <g fill="#fff">
              <rect x="6" y="12" width="2.4" height="2.4" rx="0.4" />
              <rect x="10.8" y="12" width="2.4" height="2.4" rx="0.4" />
              <rect x="15.6" y="12" width="2.4" height="2.4" rx="0.4" />
              <rect x="6" y="16" width="2.4" height="2.4" rx="0.4" />
              <rect x="10.8" y="16" width="2.4" height="2.4" rx="0.4" />
              <rect x="15.6" y="16" width="2.4" height="2.4" rx="0.4" />
            </g>
          </svg>
        </span>
      </div>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "44px",
            left: 0,
            zIndex: 60,
            background: "#fff",
            border: "1px solid #cfd6dc",
            borderRadius: "6px",
            boxShadow: "0 6px 24px rgba(0, 0, 0, 0.18)",
            padding: "10px 12px",
            width: "260px",
            boxSizing: "border-box",
          }}
        >
          {/* up-pointing caret toward the field */}
          <div
            style={{
              position: "absolute",
              top: "-7px",
              left: "18px",
              width: "14px",
              height: "14px",
              background: "#fff",
              borderLeft: "1px solid #cfd6dc",
              borderTop: "1px solid #cfd6dc",
              transform: "rotate(45deg)",
            }}
          />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
            <button
              type="button"
              onClick={() => setView(view.clone().subtract(1, "month"))}
              onMouseEnter={() => setHover("prev")}
              onMouseLeave={() => setHover("")}
              style={{ border: "none", background: hover === "prev" ? "#eef1f4" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", width: "36px", height: "36px", borderRadius: "50%", cursor: "pointer", padding: 0, transition: "background 0.15s" }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            <span style={{ fontWeight: 700, fontSize: "15px", color: "#222" }}>{view.format("MMM YYYY")}</span>
            <button
              type="button"
              onClick={() => setView(view.clone().add(1, "month"))}
              onMouseEnter={() => setHover("next")}
              onMouseLeave={() => setHover("")}
              style={{ border: "none", background: hover === "next" ? "#eef1f4" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", width: "36px", height: "36px", borderRadius: "50%", cursor: "pointer", padding: 0, transition: "background 0.15s" }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", textAlign: "center", fontSize: "14px", marginBottom: "2px" }}>
            {weekdays.map((d) => (
              <span key={d} style={{ padding: "4px 0", color: d === "Sun" ? "#d33" : "#333", fontWeight: 400 }}>
                {d}
              </span>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", textAlign: "center" }}>
            {cells.map((c, idx) => {
              const inMonth = c.month() === view.month();
              const isSun = c.isoWeekday() === 7;
              const isSelected = value && c.format("YYYY-MM-DD") === value;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => selectDay(c)}
                  style={{
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    fontSize: "15px",
                    padding: "6px 0",
                    color: !inMonth ? "#bbb" : isSun ? "#d33" : "#1a5fb4",
                    fontWeight: isSelected ? 700 : 400,
                    textDecoration: isSelected ? "underline" : "none",
                  }}
                >
                  {c.date()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export const FilterCard = ({ children, flush = false }) => (
  <div style={{ padding: flush ? "10px 0" : "10px" }}>
    <div
      style={{
        background: "#e0e6e6",
        borderTop: "2px solid #1e1e1e",
        borderBottom: "2px solid #1e1e1e",
        borderLeft: "1px solid #c3cccc",
        borderRight: "1px solid #c3cccc",
        borderRadius: "6px",
        padding: "14px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      {children}
    </div>
  </div>
);

export const ActionButton = ({ children, onClick }) => (
  <button
    onClick={onClick}
    style={{
      height: "38px",
      padding: "0 18px",
      border: "none",
      borderRadius: "4px",
      background: "#5c7b8a",
      color: "#fff",
      fontSize: "14px",
      fontWeight: 600,
      cursor: "pointer",
      alignSelf: "flex-start",
    }}
  >
    {children}
  </button>
);

export const TableCard = ({ title, children, flush = false }) => (
  <div style={{ padding: flush ? "0 0 10px" : "0 10px 10px" }}>
    <div style={{ background: "#fff", border: "1px solid #d7d7d7", borderRadius: "4px", overflow: "hidden" }}>
      <div
        style={{
          background: "linear-gradient(180deg, #33475b 0%, #243746 100%)",
          color: "#fff",
          fontWeight: 700,
          fontSize: "15px",
          padding: "10px 14px",
        }}
      >
        {title}
      </div>
      {children}
    </div>
  </div>
);

export const thStyle = {
  background: "#e9e9e9",
  color: "#111",
  fontWeight: 700,
  fontSize: "13px",
  padding: "10px 12px",
  textAlign: "center",
  border: "1px solid #ddd",
};

export const tdStyle = {
  fontSize: "13px",
  color: "#333",
  padding: "12px",
  textAlign: "center",
  border: "1px solid #ddd",
};

/** Simple entries-count + pager footer like the reference DataTables widget. */
export const EntriesHeader = ({ limit, setLimit }) => (
  <div style={{ textAlign: "center", padding: "10px", fontSize: "13px", color: "#333" }}>
    Show{" "}
    <select
      value={limit}
      onChange={(e) => setLimit(Number(e.target.value))}
      style={{ height: "28px", borderRadius: "4px", border: "1px solid #ccc", margin: "0 4px" }}
    >
      {[10, 25, 50, 100].map((n) => (
        <option key={n} value={n}>
          {n}
        </option>
      ))}
    </select>{" "}
    entries
  </div>
);

export const Pager = ({ showingText }) => (
  <div style={{ textAlign: "center", padding: "10px 0 14px" }}>
    <div style={{ fontSize: "13px", color: "#333", marginBottom: "8px" }}>{showingText}</div>
    <div style={{ display: "flex", gap: "16px", justifyContent: "center", fontSize: "13px", color: "#777" }}>
      <span>First</span>
      <span>Previous</span>
      <span
        style={{
          minWidth: "22px",
          border: "1px solid #ccc",
          borderRadius: "3px",
          padding: "0 6px",
          color: "#333",
        }}
      >
        1
      </span>
      <span>Next</span>
      <span>Last</span>
    </div>
  </div>
);
